import styled, {css} from 'styled-components';
import {getColors} from '../../utils/styling';
import React from 'react';
import {CaretIcon} from '../../atoms/Icons/CaretIcon';
import {Link} from 'gatsby';
import {NavDropdownItem} from './NavDropdown';
import {Grid} from 'react-styled-flexboxgrid';
import FlexWrapper from '../shared/FlexWrapper';
import ContactForm from '../shared/ContactForm';
import {Button, ExternalLinkButton} from '../../atoms/Button';
import {GlobalContext} from '../../context/GlobalContext';
import ReactMarkdown from 'react-markdown';
import {LoginLink} from '../../atoms/LoginLink';

const NAV_HEIGHT = 64;

const NavHamburgerButtonContainer = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  justify-content: center;
  align-items: center;
  height: 24px;
  width: 24px;
  cursor: pointer;
  transition: all 200ms ease-in-out;

  .hamburger {
    position: relative;
    width: 24px;
    height: 4px;
    background: ${({theme, variant}) => variant === 'dark' ? theme.colors.gray100 : theme.colors.gray700};
    transition: all 200ms ease-in-out;
  }

  .hamburger::before,
  .hamburger::after {
    content: "";
    position: absolute;
    width: inherit;
    height: inherit;
    background: ${({theme, variant}) => variant === 'dark' ? theme.colors.gray100 : theme.colors.gray700};
    transition: all 200ms ease-in-out;
  }

  .hamburger::before {
    transform: translateY(-8px);
  }

  .hamburger::after {
    transform: translateY(8px);
  }

  .hamburger.open {
    background: transparent;
  }

  .hamburger.open::before {
    transform: rotate(45deg);
  }

  .hamburger.open::after {
    transform: rotate(-45deg);
  }
`;

const MenuContent = styled.div`
  height: calc(100vh - ${NAV_HEIGHT}px);
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 16px 32px 32px 32px;

  > ul {
    display: block;
    flex: 0 0 auto;
    user-select: none;
    margin: 0;
    padding: 0;

    > li {
      display: block;
      min-height: 32px;
      margin: 0 0 18px 0;

      a {
        display: block;
        width: 100%;
        font-weight: 500;
        font-size: 20px;
        line-height: 32px;
        color: ${({theme, variant}) => variant === 'dark' ? theme.colors.gray100 : theme.colors.gray700};
      }
    }
  }

  .hamburger-menu-nav-action-container {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    flex: 1 0 auto;
    user-select: none;

    .login-link {
      margin-bottom: 12px;
    }
  }
`;

const NavHamburgerMenu = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1;
  display: none;
  width: 100vw;
  min-height: 100%;
  background: ${({theme, variant}) => variant === 'dark' ? theme.colors.gray800 : theme.colors.gray100};

  &.open {
    display: block;
    opacity: 1;
    transform: translateY(0%);

    animation-name: unfold;
    animation-duration: 400ms;
    animation-timing-function: cubic-bezier(0.32, 0.08, 0.24, 1);
    animation-fill-mode: forwards;

    ${MenuContent} {
      animation-name: fade-in;
      animation-duration: 600ms;
      animation-timing-function: ease-in-out;
      animation-fill-mode: forwards;
    }
  }

  .hamburger-menu-spacer {
    width: 100%;
    height: ${NAV_HEIGHT}px;
  }

  @keyframes unfold {
    from {
      transform: translateY(calc(64px - 100%));
    }
    to {
      transform: translateY(0%);
    }
  }

  @keyframes fade-in {
    0% {
      opacity: 0;
      translateZ(0); // hopefully that tricks Safari into considering this animation
    }
    50% {
      opacity: 0;
      translateZ(0);
    }
    100% {
      opacity: 1;
      translateZ(0);
    }
  }
`;

const NavHamburgerMenuItemDropdownContainer = styled.div`
  .nav-dropdown-trigger {
    outline: none;
    border: none;
    padding: 0;
    height: 24px;
    width: 100%;
    text-align: left;
    background: none;
    cursor: pointer;

    span {
      margin-right: 6px;
      font-weight: 500;
      font-size: 20px;
      line-height: 32px;
      color: ${({theme, variant}) => variant === 'dark' ? theme.colors.gray100 : theme.colors.gray700};
    }

    .caret-icon {
      margin-bottom: 2px;
      fill: ${({theme, variant}) => variant === 'dark' ? theme.colors.gray100 : theme.colors.gray700};
      transition: transform 200ms;
    }

    &.open {
      .caret-icon {
        transform: rotate(180deg);
      }
    }
  }

  .dropdown-items {
    height: auto;
    max-height: 0;
    overflow: hidden;
    transition: max-height 0.3s ease-out;

    &.open {
      max-height: 600px;
    }

    .title {
      font-weight: bold;
      font-size: 16px;
      line-height: 24px;
    }

    .description {
      font-size: 14px;
      line-height: 24px;
      color: ${({theme, variant}) => variant === 'dark' ? theme.colors.gray400 : theme.colors.gray600};
    }
  }

`;

class NavHamburgerMenuItemDropdown extends React.Component {

  constructor(props) {
    super(props);

    this.state = {isCollapsed: false};
  }

  collapseToggle() {
    this.setState({
      isCollapsed: !this.state.isCollapsed
    });
  }

  render() {

    return (
        <>
          <NavHamburgerMenuItemDropdownContainer variant={this.props.variant}>
            <button className={'nav-dropdown-trigger' + (this.state.isCollapsed ? ' open' : '')}
                    onClick={() => this.collapseToggle()}>
              <span>{this.props.title}</span>
              <CaretIcon/>
            </button>
            <div className={'dropdown-items' + (this.state.isCollapsed ? ' open' : '')}>
              {
                this.props.children.map((child, idx) => (
                    <a key={idx} href={child.link} onClick={() => this.props.callback()}>
                      <NavDropdownItem color={child.color}>
                        <p className="title">{child.title}</p>
                        {
                          child.description ?
                              <ReactMarkdown className="description" source={child.description}/> :
                              null
                        }
                      </NavDropdownItem>
                    </a>
                ))
              }
            </div>
          </NavHamburgerMenuItemDropdownContainer>
        </>
    );
  }

}

class NavHamburger extends React.Component {

  constructor(props) {
    super(props);

    this.state = {isHamburgerOpen: false};
    this.hamburgerToggle = this.hamburgerToggle.bind(this);
  }

  hamburgerToggle() {
    this.setState({
      isHamburgerOpen: !this.state.isHamburgerOpen
    }, () => {
      if (this.state.isHamburgerOpen) {
        document.body.style.overflowY = 'hidden';
      } else {
        document.body.style.overflowY = '';
      }
    });
  }

  render() {
    return (
        <>
          <NavHamburgerButtonContainer onClick={() => this.hamburgerToggle()} variant={this.props.variant}>
            <div className={'hamburger ' + (this.state.isHamburgerOpen ? 'open' : '')}/>
          </NavHamburgerButtonContainer>
          <NavHamburgerMenu className={'hamburger-menu ' + (this.state.isHamburgerOpen ? 'open' : '')}
                            variant={this.props.variant}>
            <div className="hamburger-menu-spacer"></div>
            <MenuContent variant={this.props.variant}>
              <ul className="hamburger-menu-nav-link-container">
                {
                  this.props.navbarLinks.map((navbarLink, idx) => (
                      <li key={idx}>
                        {
                          navbarLink.children && navbarLink.children.length ?
                              <NavHamburgerMenuItemDropdown title={navbarLink.title}
                                                            children={navbarLink.children}
                                                            variant={this.props.variant}
                                                            callback={() => this.hamburgerToggle()}/>
                              :
                              <a href={navbarLink.link}
                                    onClick={() => this.hamburgerToggle()}>{navbarLink.title}</a>
                        }
                      </li>
                  ))
                }
              </ul>

              <div className="hamburger-menu-nav-action-container">
                <LoginLink className="login-link" href="/dashboard" variant={this.props.variant}
                           type="hamburger-item">Login</LoginLink>
                <ExternalLinkButton primary href="https://www.dixa.com/demo">Book a Demo</ExternalLinkButton>
              </div>
            </MenuContent>
          </NavHamburgerMenu>
        </>
    );
  }
}

export {NavHamburger};
